import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import type {
  IAuditLog,
  IAuditLogDetail,
  IAuditLogFiltersResponse,
  IAuditLogListParams,
  IAuditLogSettings,
  IAuditLogStats,
} from "@/types/(company)/audit-logs.interface";

export const companyAuditLogsApi = createApi({
  reducerPath: "companyAuditLogsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["auditLogs", "auditLogSettings"],
  endpoints: (builder) => ({
    getAuditLogs: builder.query<
      IApiPaginatedResponse<IAuditLog[]>,
      IAuditLogListParams
    >({
      query: (params) => ({
        url: APIEndpoints.Company.AuditLogs.Root,
        params,
      }),
      providesTags: ["auditLogs"],
    }),
    getAuditLogById: builder.query<IApiResponse<IAuditLogDetail>, string>({
      query: (id) => ({ url: APIEndpoints.Company.AuditLogs.ById(id) }),
    }),
    getAuditLogFilterOptions: builder.query<
      IApiResponse<IAuditLogFiltersResponse>,
      void
    >({
      query: () => ({ url: APIEndpoints.Company.AuditLogs.Filters }),
    }),
    getAuditLogStats: builder.query<
      IApiResponse<IAuditLogStats>,
      { branchId?: number } | void
    >({
      query: (params) => ({
        url: APIEndpoints.Company.AuditLogs.Stats,
        params: params ?? undefined,
      }),
      providesTags: ["auditLogs"],
    }),
    getAuditLogSettings: builder.query<IApiResponse<IAuditLogSettings>, void>({
      query: () => ({ url: APIEndpoints.Company.AuditLogs.Settings }),
      providesTags: ["auditLogSettings"],
    }),
    updateAuditLogSettings: builder.mutation<
      IApiResponse<IAuditLogSettings>,
      { retentionMonths: number }
    >({
      query: (body) => ({
        url: APIEndpoints.Company.AuditLogs.Settings,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["auditLogSettings"],
    }),
  }),
});

export const {
  useGetAuditLogsQuery,
  useGetAuditLogByIdQuery,
  useGetAuditLogFilterOptionsQuery,
  useGetAuditLogStatsQuery,
  useGetAuditLogSettingsQuery,
  useUpdateAuditLogSettingsMutation,
} = companyAuditLogsApi;
